import { deriveAlt, WIDGET_REGISTRY, type WidgetType } from "@docit/doc-widgets";

/**
 * What the "insert a block" menu offers beyond prose.
 *
 * The six widget entries are GENERATED from the shared registry rather than hand-written, so
 * the menu tracks the registry: adding a widget type is one edit in one package, not two edits
 * in two that drift. Each entry carries the registry's own sample payload, so inserting one
 * gives a figure with real values in it rather than an empty frame.
 *
 * Two deliberate absences:
 *  - **No diagram entry.** Document-embedded draw.io is retired: six rows ever created, zero
 *    versions, and its collaboration room never resolved server-side, so the "Connecting to
 *    diagram collaboration…" overlay never cleared and every autosave stayed in one browser's
 *    IndexedDB. A Mermaid code fence covers the need at no infrastructure cost.
 *  - **No duplicate separator.** Upstream added a second `{name:"separator"}` before the widget
 *    group; a cosmetic double rule is not parity worth keeping.
 */

export interface BlockMenuEntry {
  id: string;
  title: string;
  /** Space-separated search terms, the way the menu filters. */
  keywords: string;
  /**
   * The node this entry inserts, with its attributes — for blocks a bare `insertContent` can
   * build. Absent when the entry runs a `command` instead.
   */
  node?: { type: string; attrs: Record<string, unknown> };
  /**
   * W10 §10 — the three MIT Tiptap blocks that cannot be inserted as a bare node.
   *
   * A `{type:"table"}` with no rows is an INVALID node (the schema requires `tableRow+`), a task
   * list is a toggle over the current block rather than an insertion, and an image has no `src`
   * until somebody has chosen a file. Each is a named command the menu runs, so this module
   * stays pure data and testable without an editor.
   */
  command?: "table" | "taskList" | "image";
  group: "widget" | "block";
}

/** Keyword sets per widget type — what somebody would actually type to find each. */
const WIDGET_KEYWORDS: Record<WidgetType, string> = {
  "chart.bars": "widget chart bars edges compare",
  "chart.gauge": "widget gauge confidence percentile",
  "chart.sparkline": "widget sparkline series trend line",
  "chart.provenance": "widget provenance evidence weight edges",
  stat: "widget stat kpi number metric tile",
  table: "widget table rows columns data",
};

export function widgetMenuEntries(): BlockMenuEntry[] {
  return (Object.keys(WIDGET_REGISTRY) as WidgetType[]).map((type) => {
    const def = WIDGET_REGISTRY[type];
    const data = def.samplePayload();
    return {
      id: `widget:${type}`,
      title: def.label,
      keywords: WIDGET_KEYWORDS[type] ?? def.keywords,
      node: {
        type: "widget",
        attrs: {
          // A JSON STRING, like everywhere else: an object attr is rewritten into the CRDT on
          // every transaction (y-prosemirror compares attrs by identity).
          data: JSON.stringify(data, null, 2),
          alt: deriveAlt(data),
        },
      },
      group: "widget",
    };
  });
}

/**
 * The non-widget additions. Mermaid replaces the retired diagram entry — the code block
 * already round-trips a ```mermaid fence, so this is a shortcut, not a new capability.
 */
export function blockMenuEntries(): BlockMenuEntry[] {
  return [
    ...widgetMenuEntries(),
    {
      id: "conversation",
      title: "Conversation",
      keywords: "conversation chat discuss thread",
      node: { type: "conversation", attrs: { conversationId: "", title: "" } },
      group: "block",
    },
    {
      id: "instruction",
      title: "Instruction",
      keywords: "instruction ask ai summarize rewrite expand",
      node: { type: "instruction_block", attrs: { instruction: "", status: "pending" } },
      group: "block",
    },
    {
      id: "table",
      title: "Table",
      keywords: "table grid rows columns spreadsheet",
      command: "table",
      group: "block",
    },
    {
      id: "task-list",
      title: "Task list",
      keywords: "task todo checkbox checklist tick",
      command: "taskList",
      group: "block",
    },
    {
      id: "image",
      title: "Image",
      keywords: "image picture photo upload attachment screenshot",
      command: "image",
      group: "block",
    },
    {
      id: "mermaid",
      title: "Mermaid diagram",
      keywords: "diagram mermaid flowchart sequence graph",
      node: { type: "codeBlock", attrs: { language: "mermaid" } },
      group: "block",
    },
  ];
}

/** The menu's filter: case-insensitive, over the title and the keywords together. */
export function filterBlockEntries(entries: BlockMenuEntry[], query: string): BlockMenuEntry[] {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return entries;
  }
  return entries.filter(
    (entry) =>
      entry.title.toLowerCase().includes(needle) || entry.keywords.toLowerCase().includes(needle),
  );
}
